import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import LanguageSelector from '../components/LanguageSelector';
import useSeo from '../hooks/useSeo';
import { schemaPrecios } from '../data/seoSchema';
import { getEmail, tienePlan } from '../lib/acceso';
import SiteFooter from '../components/SiteFooter';
import AvisameForm from '../components/AvisameForm';
import { FAQ_PRECIOS } from '../data/faqPrecios';
import { COBRO_ACTIVO } from '../lib/cobro';
import { getApiUrl } from '../lib/api';
import { useLanguage } from '../i18n/LanguageProvider';

const PLANS = [
  {
    id: 'gratis', name: 'Gratis', price: '0', period: '',
    desc: 'Descubre si hay un problema', cta: 'Probar gratis', highlight: false, free: true,
    features: ['Veredicto al instante: ¿te pagan bien o no?', 'En qué conceptos falla tu nómina', 'Detección automática del convenio', 'Nóminas ilimitadas al mes', 'Sin registro: el veredicto se ve sin dejar el correo'],
  },
  {
    id: 'trabajador', name: 'Trabajador', price: '4,99', period: ' una vez',
    desc: 'Descubre cuánto te deben. Pago único, sin renovación', cta: 'Ver cuánto me deben — 4,99 €', highlight: true,
    features: ['Todo lo del plan gratis', 'El importe exacto en euros de cada diferencia', 'Tabla comparativa: lo que cobras vs. lo que marca tu convenio', 'Informe descargable con la cita del boletín, para reclamar', '30 días de acceso: todas las nóminas que quieras', 'Un solo pago: no se renueva', 'Soporte por email'],
  },
  {
    id: 'asesoria', name: 'Asesoría / Gestoría', price: '39', period: '/mes',
    desc: 'Para despachos y gestorías', cta: 'Suscribirme', highlight: false,
    // 19-oct-2026: fuera «Informes con tu marca» y «Soporte prioritario»: el código no distingue nada por plan.
    features: ['Todo lo del plan Trabajador', 'Nóminas ilimitadas de todos tus clientes', 'Pago mensual, sin permanencia', 'Soporte por email'],
  },
];

export default function PreciosPage() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(null);
  // Plan cuyo botón ha abierto el formulario «Avísame cuando abra» (cobro pausado).
  const [avisamePlan, setAvisamePlan] = useState(null);
  // Error del pago, en la propia tarjeta: antes salía un alert() del navegador.
  const [errorPago, setErrorPago] = useState(null);

  useSeo({
    title: 'Precios de NominIA · Gratis, 4,99 € pago único o 39 €/mes para asesorías',
    description: 'Comprueba gratis si te pagan de menos. Por 4,99 €, un solo pago sin renovación, desbloqueas el desglose línea por línea y el informe PDF para reclamar. Plan de asesoría 39 €/mes. Sin permanencia.',
    path: '/precios',
    jsonLd: schemaPrecios(),
  });

  async function suscribir(plan) {
    // Cobro pausado en la web: ni se llama a /api/checkout ni se muestra un error.
    if (!COBRO_ACTIVO) { setAvisamePlan(plan); setErrorPago(null); return; }
    setLoading(plan);
    setErrorPago(null);
    try {
      const res = await axios.post(`${getApiUrl()}/api/checkout`, { plan, email: getEmail() || undefined });
      if (res.data?.url) window.location.href = res.data.url;
      else setErrorPago({ plan, msg: res.data?.error || 'No se pudo iniciar el pago. Inténtalo de nuevo en un momento.' });
    } catch (e) {
      // El servidor manda: si él tiene el cobro pausado (COBRO_ACTIVO sin poner en Railway),
      // en vez de un error se ofrece el aviso.
      if (e.response?.status === 503 && e.response?.data?.code === 'COBRO_PAUSADO') {
        setAvisamePlan(plan);
      } else {
        setErrorPago({ plan, msg: e.response?.data?.error || 'No hemos podido iniciar el pago. Inténtalo de nuevo en un momento.' });
      }
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-sans">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.svg" alt="NominIA" className="w-11 h-11 rounded-xl shadow-lg shadow-blue-500/20" />
          <div>
            <span className="text-2xl font-bold tracking-tight leading-none block">NominIA</span>
            <p className="text-xs text-gray-500 dark:text-gray-400">Verificador inteligente de nóminas</p>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSelector />
          <Link to="/" className="text-sm font-semibold text-blue-600 hover:underline">← Volver</Link>
        </div>
      </header>

      <section id="main-content" tabIndex={-1} className="max-w-6xl mx-auto px-6 pt-8 pb-20 text-center outline-none">
        {/* Mismo texto que ROUTES[precios].h1 en scripts/prerender-seo.js. */}
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">Precios de NominIA: gratis, 4,99 € pago único o 39 €/mes para asesorías</h1>
        {/* La frase que define la frontera. Si el usuario no sabe que compra, no compra. */}
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Gratis te decimos <strong>si</strong> te pagan de menos y en qué conceptos.
          Por 4,99 €, un solo pago y sin renovación, te decimos <strong>cuántos euros exactos</strong> te faltan cada mes
          y te damos el informe, con la cita del boletín oficial, para reclamárselos a tu empresa.
        </p>
        {tienePlan() && (
          <p className="mt-6 inline-block rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 px-5 py-3 text-sm font-semibold">
            Ya tienes el acceso activo en este navegador. No hace falta que vuelvas a pagar:{' '}
            <Link to="/" className="underline">vuelve a analizar tu nómina</Link>.
          </p>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-12 text-left">
          {PLANS.map((p) => (
            <div key={p.id}
              className={`rounded-3xl p-8 border transition-all ${p.highlight ? 'border-2 border-blue-600 shadow-2xl shadow-blue-500/10 scale-[1.03] bg-white dark:bg-gray-900' : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50'}`}>
              {p.highlight && <div className="inline-block mb-3 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold">MÁS POPULAR</div>}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{p.desc}</p>
              <div className="flex items-end gap-1 mb-6">
                <span className="text-5xl font-extrabold">{p.price}</span>
                <span className="text-xl font-bold mb-1">€</span>
                <span className="text-gray-400 mb-2">{p.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-green-500 font-bold">✓</span><span>{f}</span>
                  </li>
                ))}
              </ul>
              {p.free ? (
                <Link to="/" className="block text-center w-full py-3 rounded-2xl font-bold border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">{p.cta}</Link>
              ) : avisamePlan === p.id ? (
                /* Cobro pausado: en vez de un botón apagado o un error, el aviso. */
                <div className="rounded-2xl border border-lime-300 dark:border-lime-900/50 bg-lime-50/60 dark:bg-lime-900/10 p-4">
                  <p className="font-bold text-sm mb-3">{t('avisame.boton')}</p>
                  <AvisameForm origen="precios" plan={p.id} compacto onCerrar={() => setAvisamePlan(null)} />
                </div>
              ) : (
                <>
                  <button type="button" onClick={() => suscribir(p.id)} disabled={loading === p.id}
                    className={`w-full py-3 rounded-2xl font-bold transition-all disabled:opacity-50 ${!COBRO_ACTIVO ? 'bg-[#0E2438] hover:bg-[#0A1A2B] text-white' : p.highlight ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20' : 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:opacity-90'}`}>
                    {loading === p.id ? 'Redirigiendo…' : (!COBRO_ACTIVO ? t('avisame.boton') : p.cta)}
                  </button>
                  {!COBRO_ACTIVO && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">{t('avisame.leadCorto')}</p>}
                  {errorPago && errorPago.plan === p.id && <p className="text-sm text-red-600 mt-2" role="alert">{errorPago.msg}</p>}
                </>
              )}
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300 mt-10 max-w-2xl mx-auto">
          <strong>Ningún plan tiene límite de uso:</strong> puedes analizar todas las nóminas que
          quieras cada mes, también en el plan gratuito. No cobramos por análisis ni hay bolsas de
          créditos que se agoten.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          {COBRO_ACTIVO ? 'Pago seguro con Stripe.' : 'Los planes de pago abren próximamente; el análisis gratis funciona ya.'} Tus datos están protegidos — <Link to="/privacidad" className="text-blue-600 hover:underline">política de privacidad</Link>.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          ¿Quieres ver primero contra qué se compara tu nómina? Consulta las{' '}
          <Link to="/convenios" className="text-blue-600 hover:underline">tablas salariales por convenio</Link> con su fuente oficial.
        </p>

        {/* Las objeciones que frenan el pago, respondidas antes del boton. Mismo
            contenido que el FAQPage del JSON-LD y que el HTML del prerender. */}
        <section aria-labelledby="faq-precios" className="mt-16 max-w-3xl mx-auto text-left">
          <h2 id="faq-precios" className="text-2xl font-bold tracking-tight mb-6 text-center">Preguntas frecuentes</h2>
          <div className="divide-y divide-gray-200 dark:divide-gray-800 rounded-3xl border border-gray-200 dark:border-gray-800">
            {FAQ_PRECIOS.map((f) => (
              <details key={f.p} className="group px-6 py-4">
                <summary className="cursor-pointer list-none font-semibold flex items-center justify-between gap-4">
                  <span>{f.p}</span>
                  <span aria-hidden="true" className="text-blue-600 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{f.r}</p>
              </details>
            ))}
          </div>
        </section>
      </section>
      <SiteFooter />
    </div>
  );
}
